import React from "react";
import api, { postForm } from "../../api/client";
import { useOrg } from "../../context/OrgContext";
import { useTerminology } from "../../context/TerminologyContext";
import { withOrgQS } from "../../utils/url";
import PdfViewer from "../../components/PdfViewer";
import { useAuth } from "../../context/AuthContext";
import PinModal, { usePinGate } from "../../components/PinModal";
import PageHeader from "../../components/PageHeader";
import {
  DateRangePicker,
  InlineFieldInfo,
  ModalShell,
  Pager,
  usePagination,
  formatChecklistMonth,
  getChecklistMonthHeaderValue,
  isChecklistMonthLabel,
  isMrpHeaderLabel,
} from "../../components/ui-helpers";
import { formatConfidence, formatDateTime } from "../../utils/datetime";

function violatesThreshold(count, rule) {
  if (
    !rule ||
    !rule.criteria ||
    String(rule.criteria).trim().toLowerCase() === "nil"
  )
    return false;
  const value = Number(count ?? 0);
  const need = Number(rule.count ?? 0);
  const criteria = String(rule.criteria || "")
    .trim()
    .toLowerCase();
  if (criteria === "max") return value > need;
  if (criteria === "min") return value < need;
  if (criteria === "equals") return value !== need;
  return false;
}

function headerDisplayValue(values, label) {
  if (!values) return "";
  const header = values.header || {};
  const counts = values.counts || {};
  const key = String(label).trim().toLowerCase().replace(/\s+/g, " ");
  if (key === "enter yes:") return counts.Y ?? counts.y ?? header[label] ?? "";
  if (key === "enter no:") return counts.N ?? counts.n ?? header[label] ?? "";
  if (key === "enter n/a:")
    return counts.NA ?? counts.na ?? header[label] ?? "";
  const rawValue = isChecklistMonthLabel(label)
    ? getChecklistMonthHeaderValue(header, label)
    : (header[label] ?? header[label?.replace(/:\s*$/, " :")] ?? "");
  return isChecklistMonthLabel(label) ? formatChecklistMonth(rawValue) : rawValue;
}

function headerCountStyle(values, thr, label) {
  if (!values || !thr || !label) return {};
  const header = values.header || {};
  const counts = values.counts || {};
  const key = String(label).trim().toLowerCase().replace(/\s+/g, " ");
  const code =
    key === "enter yes:"
      ? "Y"
      : key === "enter no:"
        ? "N"
        : key === "enter n/a:"
          ? "NA"
          : null;
  if (!code) return {};
  const value =
    counts[code] ??
    counts[code.toLowerCase?.()] ??
    header[label] ??
    header[label.replace(/:\s*$/, " :")];
  const rule =
    thr?.[code] || thr?.[code.toUpperCase?.()] || thr?.[code.toLowerCase?.()];
  return violatesThreshold(value, rule)
    ? {
        background: "rgba(254, 226, 226, 0.92)",
        color: "#991b1b",
        fontWeight: 700,
        borderRadius: 10,
        padding: "2px 8px",
        display: "inline-block",
      }
    : {};
}

function mrpDisplayMeta(chk, label) {
  const cfg = chk?.mrp_config || {};
  if (!isMrpHeaderLabel(label)) return { style: {}, info: "" };
  return {
    info: cfg.has_std_mrp ? cfg.info_text || "" : "",
    style:
      cfg.has_threshold && cfg.violates_threshold
        ? {
            background: "rgba(254, 226, 226, 0.92)",
            color: "#991b1b",
            fontWeight: 700,
            borderRadius: 10,
            padding: "2px 8px",
            display: "inline-block",
          }
        : {},
  };
}

function toPairs(headerTemplate, values) {
  const pairs = headerTemplate?.pairs;
  if (pairs && pairs.length) return pairs;
  const header = values?.header || {};
  const keys = Object.keys(header)
    .filter((key) => String(key).trim())
    .sort((a, b) => a.localeCompare(b));
  const out = [];
  for (let index = 0; index < keys.length; index += 2)
    out.push(keys.slice(index, index + 2));
  return out;
}

function isLocalDateInRange(value, range) {
  const raw = String(value || "").slice(0, 10);
  if (!raw) return true;
  if (range?.date_from && raw < range.date_from) return false;
  if (range?.date_to && raw > range.date_to) return false;
  return true;
}

export default function ReviewCoasPage() {
  const { user } = useAuth();
  const { current: org } = useOrg();
  const { t } = useTerminology();
  const [pending, setPending] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [sortBy, setSortBy] = React.useState("last_action_time");
  const [sortOrder, setSortOrder] = React.useState("desc");
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [selected, setSelected] = React.useState(null);
  const [banner, setBanner] = React.useState({ type: "", text: "" });
  const [pendingTableOpen, setPendingTableOpen] = React.useState(true);
  const pinGate = usePinGate();
  const [decisionModal, setDecisionModal] = React.useState({
    open: false,
    mode: "Rejected",
    comments: "",
  });

  const [checklistEnabled, setChecklistEnabled] = React.useState(true);
  const [crit, setCrit] = React.useState({ op: "<", val: 70 });
  const [pdfUrl, setPdfUrl] = React.useState("");
  const [specs, setSpecs] = React.useState([]);
  const [raw, setRaw] = React.useState([]);
  const [edited, setEdited] = React.useState([]);
  const [conf, setConf] = React.useState([]);
  const [extractionMeta, setExtractionMeta] = React.useState(null);
  const [checks, setChecks] = React.useState(null);
  const [chk, setChk] = React.useState(null);
  const [chkUrl, setChkUrl] = React.useState("");
  const [thrCfg, setThrCfg] = React.useState(null);

  const reload = React.useCallback(async () => {
    try {
      const response = await api.get("/manager/review_list");
      setPending(response.data?.items || []);
    } catch {
      setPending([]);
    }
  }, []);

  React.useEffect(() => {
    reload().catch(() => {});
  }, [reload, org]);

  React.useEffect(() => {
    api
      .get("/orgs/settings")
      .then((response) => {
        const settings = response.data || {};
        setChecklistEnabled(!!settings.checklist_enabled);
        setCrit({
          op: settings.crit_op || "<",
          val: parseInt(settings.crit_threshold ?? 70, 10),
        });
      })
      .catch(() => {
        setChecklistEnabled(true);
        setCrit({ op: "<", val: 70 });
      });
  }, [org]);

  const visiblePending = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (pending || []).filter((item) => {
      if (
        !isLocalDateInRange(
          item.last_action_time || item.upload_time,
          dateRange,
        )
      )
        return false;
      if (!needle) return true;
      return `${item.user || ""} ${item.vendor || ""} ${item.material || ""} ${item.sku || ""} ${item.lot_no || ""} ${item.status || ""}`
        .toLowerCase()
        .includes(needle);
    });
    rows.sort((a, b) => {
      const aVal = String(a?.[sortBy] ?? "");
      const bVal = String(b?.[sortBy] ?? "");
      const cmp = aVal.localeCompare(bVal, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortOrder === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [pending, search, sortBy, sortOrder, dateRange]);

  const pager = usePagination(visiblePending, 10);

  React.useEffect(() => {
    const stillThere =
      selected &&
      pending.some(
        (item) =>
          item.vendor === selected.vendor &&
          item.sku === selected.sku &&
          item.lot_no === selected.lot_no &&
          String(item.material_id || "") === String(selected.material_id || ""),
      );
    if (selected && !stillThere) {
      setSelected(null);
      setPendingTableOpen(true);
    }
  }, [pending, selected]);

  React.useEffect(() => {
    let cancelled = false;

    const loadDetails = async () => {
      setPdfUrl("");
      setSpecs([]);
      setRaw([]);
      setEdited([]);
      setConf([]);
      setExtractionMeta(null);
      setChecks(null);
      setChk(null);
      setChkUrl("");
      setThrCfg(null);

      if (!selected) return;

      try {
        const pdfParams = new URLSearchParams({
          vendor: selected.vendor,
          sku: selected.sku,
          lot_no: selected.lot_no,
          ...(selected.material_id
            ? { material_id: selected.material_id }
            : {}),
        });
        const pdf = `${process.env.REACT_APP_API_BASE}/user/get_processed_pdf?${pdfParams.toString()}`;
        if (!cancelled) setPdfUrl(withOrgQS(pdf, org));

        const response = await api.get("/manager/review_get", {
          params: {
            vendor: selected.vendor,
            sku: selected.sku,
            lot_no: selected.lot_no,
            ...(selected.material_id
              ? { material_id: selected.material_id }
              : {}),
          },
        });

        if (cancelled) return;
        setSpecs(response.data?.specs || []);
        const nextSpecs = response.data?.specs || [];
        const rawConf = response.data?.confidences || [];
        setRaw(response.data?.raw_results || []);
        setEdited(response.data?.edited_results || []);
        setConf(
          nextSpecs.map((spec, index) => {
            const cautionYes =
              String(spec?.Caution || "")
                .trim()
                .toLowerCase() === "yes";
            return cautionYes ? 0.5 : (rawConf[index] ?? 0);
          }),
        );
        setExtractionMeta(response.data?.meta || null);
        setChecks(response.data?.checks || null);
        setPendingTableOpen(false);

        if (!checklistEnabled) return;

        const savedChecklist = response.data?.checklist || {};
        try {
          const checklistRes = await api.get("/checklists/get", {
            params: {
              vendor: selected.vendor,
              sku: selected.sku,
              lot_no: selected.lot_no,
              ...(selected.material_id
                ? { material_id: selected.material_id }
                : {}),
            },
          });
          if (cancelled) return;
          const template = checklistRes.data || {};
          template.header_template = template.header_template || {};
          if (
            !Array.isArray(template.header_template.pairs) ||
            !template.header_template.pairs.length
          ) {
            template.header_template.pairs = toPairs(
              template.header_template,
              savedChecklist,
            );
          }
          setChk({ ...template, values: savedChecklist });
          const checklistPdf = `${process.env.REACT_APP_API_BASE}/user/get_checklist_pdf?${new URLSearchParams({ vendor: selected.vendor, sku: selected.sku, lot_no: selected.lot_no, ...(selected.material_id ? { material_id: selected.material_id } : {}) }).toString()}`;
          setChkUrl(withOrgQS(checklistPdf, org));
          if (template?.threshold) {
            setThrCfg(template.threshold);
          } else {
            try {
              const thresholdRes = await api.get("/checklists/threshold", {
                params: { material_type: template?.type || "Others" },
              });
              if (!cancelled) setThrCfg(thresholdRes.data?.threshold || null);
            } catch {
              if (!cancelled) setThrCfg(null);
            }
          }
        } catch {
          if (!cancelled) {
            setChk(null);
            setChkUrl("");
            setThrCfg(null);
          }
        }
      } catch {
        if (!cancelled) {
          setBanner({
            type: "error",
            text: "Unable to load the selected COA review package.",
          });
        }
      }
    };

    loadDetails().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selected, org, checklistEnabled]);

  const decide = async (decision, comments = "") => {
    if (!selected) return;
    try {
      await postForm("/manager/review_decide", {
        vendor: selected.vendor,
        sku: selected.sku,
        material_id: selected.material_id || "",
        lot_no: selected.lot_no,
        decision,
        manager_id: user.uid,
        comments,
      });
      setBanner({ type: "success", text: `${decision} recorded.` });
      setSelected(null);
      setPendingTableOpen(true);
      await reload();
    } catch (err) {
      setBanner({
        type: "error",
        text:
          err?.response?.data?.error ||
          `Unable to record ${decision.toLowerCase()}.`,
      });
    }
  };

  const submitDecisionModal = () => {
    const { mode, comments } = decisionModal;
    setDecisionModal({ open: false, mode: "Rejected", comments: "" });
    pinGate.askPinThen(() => decide(mode, comments));
  };

  const extractionFailed =
  String(extractionMeta?._extraction_status || "").toLowerCase() === "failed";

  const confidenceDanger = (value) => {
    const pct = Math.round(100 * (Number(value) || 0));
    switch (crit.op) {
      case ">":
        return pct > crit.val;
      case "=":
        return pct === crit.val;
      case "<":
      default:
        return pct < crit.val;
    }
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Manager"
        icon="document"
        title="Review COAs"
        subtitle={`Browse pending COAs in the request table, select a single ${t("lot").toLowerCase()} to review to reveal approval decisions.`}
      />

      {banner.text ? (
        <div
          className={`banner ${banner.type === "error" ? "error" : "success"}`}
        >
          {banner.text}
        </div>
      ) : null}

      <div className="section">
        <details
          className="selection-expander"
          open={pendingTableOpen}
          onToggle={(event) => setPendingTableOpen(event.currentTarget.open)}
        >
          <summary>Choose a pending COA</summary>
          <div className="helper-text" style={{ marginBottom: 10 }}>
            Search, sort, and select a pending {t("lot").toLowerCase()} to open
            its PDF, extraction details, checklist, and manager actions.
          </div>
          <div className="filters-bar">
            <label>
              <span>Search pending COAs</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, or user`}
              />
            </label>
            <label>
              <span>Sort by</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="last_action_time">Last action</option>
                <option value="vendor">{t("vendor")}</option>
                <option value="material">{t("material")}</option>
                <option value="sku">SKU</option>
                <option value="lot_no">{t("lot")}</option>
                <option value="status">Status</option>
              </select>
            </label>
            <label>
              <span>Direction</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </label>
            <DateRangePicker
              value={dateDraft}
              onChange={setDateDraft}
              onApply={() => setDateRange({ ...dateDraft })}
              onClear={(empty) => setDateRange(empty)}
              label="Last action range"
            />
          </div>

          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense review-coas-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>{t("vendor")}</th>
                    <th>{t("material")}</th>
                    <th>SKU</th>
                    <th>{t("lot")}</th>
                    <th>Status</th>
                    <th>Last action</th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageData.map((row) => {
                    const active =
                      selected &&
                      row.vendor === selected.vendor &&
                      row.sku === selected.sku &&
                      row.lot_no === selected.lot_no &&
                      String(row.material_id || "") ===
                        String(selected.material_id || "");
                    return (
                      <tr
                        key={`${row.vendor}-${row.sku}-${row.lot_no}`}
                        onClick={() => setSelected(row)}
                        className={active ? "is-selected" : undefined}
                      >
                        <td>{row.user}</td>
                        <td>{row.vendor}</td>
                        <td>{row.material}</td>
                        <td>{row.sku}</td>
                        <td>{row.lot_no}</td>
                        <td>
                          <span className="status-pill status-pending">
                            {row.status}
                          </span>
                        </td>
                        <td>
                          {formatDateTime(
                            row.last_action_time || row.upload_time,
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {visiblePending.length === 0 ? (
                    <tr>
                      <td colSpan={7}>No pending COAs found.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <Pager {...pager} />
          </div>
        </details>
      </div>

      <div className="section">
        <div className="page-header-row">
          <div>
            <h3>
              {selected
                ? `${selected.vendor} · ${selected.sku} · ${selected.lot_no}`
                : "Select a COA to review"}
            </h3>
            {!selected ? (
              <div className="helper-text">No COA selected.</div>
            ) : null}
          </div>
          {selected ? (
            <span className="status-pill status-pending">Ready for review</span>
          ) : null}
        </div>

        {selected ? (
          <>
            <div className="two-pane">
              <div className="pane pdf-pane">
                {pdfUrl ? (
                  <PdfViewer url={pdfUrl} />
                ) : (
                  <div className="section">Loading COA PDF…</div>
                )}
              </div>
              <div className="pane table-pane">
                <div className="table--card">
                  <div className="table-scroll">
                    <table className="table table--dense">
                      <thead>
                        <tr>
                          <th>Parameter</th>
                          <th>Raw</th>
                          <th>Edited</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {specs.map((spec, index) => {
                          const low = confidenceDanger(conf[index]);
                          return (
                            <tr key={`${spec.Parameter}-${index}`}>
                              <td>{spec.Parameter}</td>
                              <td
                                style={
                                  low
                                    ? {
                                        background: "rgba(254, 226, 226, 0.75)",
                                        color: "#991b1b",
                                        fontWeight: 700,
                                      }
                                    : undefined
                                }
                              >
                                {raw[index] ?? ""}
                              </td>
                              <td>{edited[index] ?? ""}</td>
                              <td>
                                <span
                                  className={
                                    low
                                      ? "confidence-chip confidence-chip--warn"
                                      : "confidence-chip"
                                  }
                                >
                                  {formatConfidence(conf[index])}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {specs.length === 0 ? (
                          <tr>
                            <td colSpan={4}>No extraction rows available.</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {checks ? (
              <div className="section">
                <h3>Checks</h3>
                {checklistEnabled &&
                Array.isArray(checks.header_flags) &&
                checks.header_flags.length > 0 ? (
                  <div className="banner error">
                    {checks.header_flags.join(" • ")}
                  </div>
                ) : null}
                <div className="grid grid-2">
                  <div className="table--card">
                    <h4 style={{ margin: "12px 14px 0" }}>OOS</h4>
                    <div className="table-scroll">
                      <table className="table table--dense">
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Current</th>
                            <th>LL</th>
                            <th>UL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(checks.oos || []).map((row, index) => (
                            <tr key={index}>
                              <td>{row.Parameter}</td>
                              <td>{row.Current}</td>
                              <td>{row.Lower}</td>
                              <td>{row.Upper}</td>
                            </tr>
                          ))}
                          {(checks.oos || []).length === 0 ? (
                            <tr>
                              <td colSpan={4}>No OOS observations.</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="table--card">
                    <h4 style={{ margin: "12px 14px 0" }}>OOT</h4>
                    <div className="table-scroll">
                      <table className="table table--dense">
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Current</th>
                            <th>95% Lower</th>
                            <th>95% Upper</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(checks.oot || []).map((row, index) => (
                            <tr key={index}>
                              <td>{row.Parameter}</td>
                              <td>{row.Current}</td>
                              <td>{row["95 lower"]}</td>
                              <td>{row["95 upper"]}</td>
                            </tr>
                          ))}
                          {(checks.oot || []).length === 0 ? (
                            <tr>
                              <td colSpan={4}>No OOT observations.</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="table--card" style={{ gridColumn: "1 / -1" }}>
                    <h4 style={{ margin: "12px 14px 0" }}>OOGT</h4>
                    <div className="table-scroll">
                      <table className="table table--dense">
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>General pattern</th>
                            <th>Current</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(checks.oogt || []).map((row, index) => (
                            <tr key={index}>
                              <td>{row.Parameter}</td>
                              <td>{row["General Pattern"]}</td>
                              <td>{row.Current}</td>
                            </tr>
                          ))}
                          {(checks.oogt || []).length === 0 ? (
                            <tr>
                              <td colSpan={3}>No OOGT observations.</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {checklistEnabled && chk ? (
              <div className="section">
                <h3>Checklist</h3>
                <div className="two-pane">
                  <div className="pane pdf-pane">
                    {chkUrl ? (
                      <PdfViewer url={chkUrl} />
                    ) : (
                      <div className="section">Checklist PDF unavailable.</div>
                    )}
                  </div>
                  <div className="pane table-pane">
                    <div className="table--card">
                      <div className="table-scroll">
                        <table className="table table--tight">
                          <tbody>
                            {toPairs(chk.header_template, chk.values).map(
                              (pair, index) => (
                                <tr key={index}>
                                  {pair.map((label) => (
                                    <React.Fragment key={label}>
                                      <td style={{ width: 180 }}>
                                        <strong>{label}</strong>
                                        {mrpDisplayMeta(chk, label).info ? (
                                          <InlineFieldInfo
                                            text={
                                              mrpDisplayMeta(chk, label).info
                                            }
                                            label={`${label} MRP information`}
                                          />
                                        ) : null}
                                      </td>
                                      <td
                                        style={{
                                          ...headerCountStyle(
                                            chk.values,
                                            thrCfg,
                                            label,
                                          ),
                                          ...mrpDisplayMeta(chk, label).style,
                                        }}
                                      >
                                        {String(
                                          headerDisplayValue(chk.values, label),
                                        )}
                                      </td>
                                    </React.Fragment>
                                  ))}
                                  {pair.length === 1 ? (
                                    <>
                                      <td />
                                      <td />
                                    </>
                                  ) : null}
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ padding: 16 }}>
                        <div>
                          <strong>Question:</strong> {chk.question?.text}{" "}
                          {chk.question?.code ? (
                            <small>({chk.question.code})</small>
                          ) : null}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <strong>Answer:</strong>{" "}
                          <span
                            className={
                              String(
                                chk.values?.answer || "N/A",
                              ).toUpperCase() === "Y"
                                ? "status-pill status-rejected"
                                : "status-pill status-approved"
                            }
                          >
                            {String(chk.values?.answer || "N/A")}
                          </span>
                          {String(chk.values?.answer || "").toUpperCase() ===
                          "Y" ? (
                            <span style={{ marginLeft: 10 }}>
                              <strong>Deviation No.:</strong>{" "}
                              {chk.values?.deviation_no || ""}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="helper-text">
            Choose a row from the request table to open its PDF, extraction
            details, checklist, and manager actions.
          </div>
        )}
      </div>

      {selected ? (
        <div className="section">
          {extractionFailed ? (
            <div className="banner warn">
              This COA has no usable extraction result. Review the document and specifications, then Reject or request re-upload. Approval is unavailable.
            </div>
          ) : null}
          <div className="card-actions">
            <button
              className="btn btn-primary"
              type="button"
              disabled={!extractionMeta || extractionFailed}
              onClick={() => pinGate.askPinThen(() => decide("Approved"))}
            >
              Approve
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() =>
                setDecisionModal({ open: true, mode: "Rejected", comments: "" })
              }
            >
              Reject
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() =>
                setDecisionModal({
                  open: true,
                  mode: "Invalidated",
                  comments: "",
                })
              }
            >
              Re-upload
            </button>
          </div>
        </div>
      ) : null}

      <ModalShell
        open={decisionModal.open}
        onClose={() =>
          setDecisionModal({ open: false, mode: "Rejected", comments: "" })
        }
        title={`${decisionModal.mode} — add comments`}
        subtitle="Comments are optional but useful when rejecting or invalidating a COA."
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() =>
                setDecisionModal({
                  open: false,
                  mode: "Rejected",
                  comments: "",
                })
              }
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitDecisionModal}
            >
              Continue
            </button>
          </>
        }
      >
        <label>
          Comments
          <textarea
            rows={5}
            value={decisionModal.comments}
            onChange={(e) =>
              setDecisionModal((current) => ({
                ...current,
                comments: e.target.value,
              }))
            }
          />
        </label>
      </ModalShell>

      <PinModal
        uid={user?.uid || ""}
        open={pinGate.pinOpen}
        onClose={pinGate.closePin}
        onVerified={pinGate.handlePinVerified}
      />
    </div>
  );
}
